import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from "react-redux";
import { message, Spin, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import QuizCard from "../../components/QuizCard/QuizCard";
import usePracticeSession from "../../hooks/usePracticeSession";
import useTranslation from '../../hooks/useTranslation';

const QuizRunPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Методику выбирает страница выбора практики и кладёт в redux вместе с
    // practiceId: жёсткая константа здесь открывала бы сессию не той политикой,
    // которой практика была создана.
    const { practiceId, learningMethod } = useSelector((state) => state.practice);

    // Сессия ведётся локально: вопросы приходят окном, ответы уходят пачкой.
    const { item, isLoading, finished, error, answer } = usePracticeSession({
        practiceId,
        learningMethod,
    });

    const [selectedOptions, setSelectedOptions] = useState(new Set());
    const [checked, setChecked] = useState(false);
    // Итог на момент проверки: после неё выбор ещё редактируется.
    const checkedResult = useRef(null);

    // Верность ответа считает клиент — так было и раньше: сервер получает факт
    // «ответил верно», а не сами варианты.
    const isMultipleCorrect = Boolean(item)
        && item.answers.filter((option) => option.isCorrect).length > 1;

    // Смена вопроса сбрасывает выбор: иначе галочки предыдущего переносились бы
    // на следующий, а окно меняет вопрос без перезагрузки страницы.
    useEffect(() => {
        setSelectedOptions(new Set());
        setChecked(false);
        checkedResult.current = null;
    }, [item]);

    const handleOptionChange = (event) => {
        if (isMultipleCorrect) {
            const next = new Set(selectedOptions);
            if (next.has(event.target.value)) {
                next.delete(event.target.value);
            } else {
                next.add(event.target.value);
            }
            setSelectedOptions(next);
        } else {
            setSelectedOptions(new Set([event.target.value]));
        }
    };

    const isAnswerCorrect = (answerText) =>
        Boolean(item) && item.answers.some((option) => option.text === answerText && option.isCorrect);

    // Правильность фиксируется при нажатии «проверить», а не «дальше»: после проверки
    // верные варианты видны, и выбор ещё можно поправить. Записать в журнал итог такой
    // правки значило бы назвать подсмотренный ответ верным с первой попытки.
    const handleCheckClick = () => {
        setChecked(true);
        checkedResult.current = isMultipleCorrect
            ? Array.from(selectedOptions).every((option) => isAnswerCorrect(option))
            : isAnswerCorrect([...selectedOptions][0] || '');
    };

    const handleNextClick = () => {
        if (!item) {
            message.error(t('practice.run.noItem'));
            return;
        }
        const correct = checkedResult.current ?? (isMultipleCorrect
            ? Array.from(selectedOptions).every((option) => isAnswerCorrect(option))
            : isAnswerCorrect([...selectedOptions][0] || ''));
        checkedResult.current = null;
        // Число вариантов идёт вместе с ответом: верный выбор из четырёх — более слабое
        // свидетельство, чем воспроизведение, и учесть это можно только зная их число.
        answer(correct, { optionCount: item.answers?.length ?? null });
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <Spin tip={t('practice.run.loading')} size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <p>{error}</p>
                <Button type="primary" onClick={() => navigate('/learn')}>
                    {t('practice.run.back')}
                </Button>
            </div>
        );
    }

    // Конец практики приходит признаком в ответе, а не текстом ошибки: раньше здесь
    // сравнивалась формулировка «No active questions were found.», и правка этого
    // сообщения на бэкенде молча заменила бы поздравление на ошибку.
    if (finished) {
        return (
            <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <p>{t('practice.run.finished')}</p>
                <Button type="primary" onClick={() => navigate('/learn')}>
                    {t('practice.run.back')}
                </Button>
            </div>
        );
    }

    return item ? (
        <QuizCard
            quiz={item}
            isMultipleCorrect={isMultipleCorrect}
            selectedOptions={selectedOptions}
            handleOptionChange={handleOptionChange}
            checked={checked}
            handleCheckClick={handleCheckClick}
            handleNextClick={handleNextClick}
            isAnswerCorrect={isAnswerCorrect}
        />
    ) : (
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <p>{t('practice.run.noItem')}</p>
        </div>
    );
};

export default QuizRunPage;
